import { createContext, type PropsWithChildren, useContext } from 'react';
import type { Token } from '@/api/spec';
import type { Side } from '@/common/constants';
import type { MarketData } from '@/common/MarketData';
import type { FillPrognosis } from '@/feature/order_book/types';
import type { OrderValidationFunction } from './order_schema';

export type { FillPrognosis };

export type GetMarketFillPrognosis = (side: Side, assetSize: number, slippagePct?: number) => FillPrognosis | undefined;

const OrderFormContext = createContext<OrderFormContextValue | undefined>(undefined);

export function useOrderFormContext() {
  const context = useContext(OrderFormContext);
  if (context == null) {
    throw new Error('useOrderFormContext must be used within a OrderFormProvider');
  }
  return context;
}
interface OrderFormContextValue {
  accountValue?: bigint;
  collateralToken?: Token;
  marketData?: MarketData;
  // Account fee tier (Account.ft) — indexes the fee ladders on MarketData; undefined → base fees.
  feeTier?: number;
  feeTiersEnabled?: boolean;
  disabled?: boolean;
  tradingEnabled?: boolean;
  triggerOrdersEnabled?: boolean;
  onPrefillMidPrice?: (side: Side) => void;
  validateOrder?: OrderValidationFunction;
  // Implementer must read non-reactively — this is called on every form render.
  getMarketFillPrognosis?: GetMarketFillPrognosis;
  marketOrderImpactWarnPct?: number;
  // Effective slippage cap (percent, e.g. 0.5 for 0.5%) — custom user setting if enabled,
  // otherwise the per-market default (`orderMaxImpactP`). Undefined when neither is known.
  currentSlippagePct?: number;
  // Custom-slippage feature flag; gates the "Edit" affordance in the fill prognosis (read-only when false).
  slippageEditable?: boolean;
  // Maker-volume mPoints boost campaign; gates the Limit tab dot and the limit-form notice.
  makerBoostEnabled?: boolean;
}

export function OrderFormProvider({
  children,
  accountValue,
  collateralToken,
  marketData,
  feeTier,
  feeTiersEnabled,
  disabled,
  tradingEnabled,
  triggerOrdersEnabled,
  onPrefillMidPrice,
  validateOrder,
  getMarketFillPrognosis,
  marketOrderImpactWarnPct,
  currentSlippagePct,
  slippageEditable,
  makerBoostEnabled,
}: PropsWithChildren<OrderFormContextValue>) {
  return (
    <OrderFormContext.Provider
      value={{
        accountValue,
        collateralToken,
        marketData,
        feeTier,
        feeTiersEnabled,
        disabled,
        tradingEnabled,
        triggerOrdersEnabled,
        onPrefillMidPrice,
        validateOrder,
        getMarketFillPrognosis,
        marketOrderImpactWarnPct,
        currentSlippagePct,
        slippageEditable,
        makerBoostEnabled,
      }}
    >
      {children}
    </OrderFormContext.Provider>
  );
}
